import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import ChangelogMenu from "@/components/changelog/changelog-menu";
import ChangelogWidget from "@/components/changelog/changelog-widget";
import ScannerButton from "@/pages/(app)/shop/components/sidebar/scanner-button.tsx";
import { useAuth } from "@babackapp/auth-client/hooks";
import useAllViewerShops from "@babackapp/private-api-client/client/use-all-viewer-shops";
import { useSuperAdminAllShops } from "@babackapp/private-api-client/client/use-super-admin-all-shops";
import { useViewerIsBabackTeam } from "@/lib/rights/use-viewer-is-baback-team";
import useTranslation from "@babackapp/translations/use-translation";
import type { AccountSwitcherProps } from "@babackapp/ui/components/account-switcher";
import AccountSwitcher from "@babackapp/ui/components/account-switcher";
import type { Shop } from "@babackapp/ui/components/shop-logo";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarRail,
  useSidebar,
} from "@babackapp/ui/components/sidebar";
import { ExternalLink, Warehouse } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { splitFoundShops } from "./split-found-shops";
import useViewerId from "@/api/user/use-viewer-id";
import NavigationContainer from "./container";
import SidebarLogo from "./logo.tsx";
import DemoReturnWidgetButton from "./demo-return-widget-button";
import NewReturnButton from "./new-return-button";
import PrimaryNavigation from "./primary-navigation.tsx";
import SecondaryNavigation from "./secondary-navigation.tsx";

export type User = AccountSwitcherProps["user"];

export default function SideBar() {
  return (
    <NavigationContainer>
      <WrappedAccountSwitcher />
      <NewReturnButton />
      <PrimaryNavigation />
      <SecondaryNavigation />
      <div className="flex-1" />
      <ScannerButton />
    </NavigationContainer>
  );
}

function WrappedAccountSwitcher() {
  const t = useTranslation();
  const navigate = useNavigate();
  const { data: currentShop } = useCurrentShopSuspense();
  const { data: shops } = useAllViewerShops();
  const auth = useAuth();
  const { user } = auth;
  const isBabackTeam = useViewerIsBabackTeam();
  // A super admin reaches every shop (BAB-3035): beyond their own, the switcher
  // lists all of them, loaded once it opens (not on every page of the shop),
  // then cached, and filtered as one types.
  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);
  const { data: allShops, isPending } = useSuperAdminAllShops(
    isBabackTeam && isSwitcherOpen,
  );
  const { ownShops, otherShops } = splitFoundShops(shops ?? [], allShops ?? []);

  if (!currentShop || !shops || !user) return null;

  return (
    <AccountSwitcher
      currentShop={currentShop as Shop}
      user={user as User}
      shops={ownShops as Shop[]}
      onShopSelect={(shop) => {
        if (shop.id !== currentShop.id) {
          navigate(`/shops/${shop.id}`);
        }
      }}
      onSettingsClick={() => {
        navigate(`/shops/${currentShop.id}/account/settings`);
      }}
      onLogout={async () => {
        await auth.logout();
      }}
      noResultLabel={t("sidebar.noResult")}
      settingsLabel={t("sidebar.accountPreferences")}
      logoutLabel={t("misc.disconnect")}
      shopsHeading={t("sidebar.sections.dashboard")}
      settingsHeading={t("sidebar.sections.settings")}
      {...(isBabackTeam && {
        onAdminClick: () => navigate("/admin"),
        adminLabel: "Super Admin",
        onOpenChange: setIsSwitcherOpen,
        searchPlaceholder: t("sidebar.sections.searchAllShops"),
        remoteShopGroup: {
          heading: t("sidebar.sections.allShops"),
          // No logos: a thousand of them would load on each opening.
          shops: otherShops.map((shop) => ({
            ...shop,
            logoUrl: undefined,
          })) as Shop[],
          isLoading: isSwitcherOpen && isPending,
          loadingLabel: t("sidebar.sections.allShopsLoading"),
        },
      })}
    />
  );
}

function LogisticianWarehouse() {
  const { data: currentShop } = useCurrentShopSuspense();
  const { data: viewerId } = useViewerId();
  const { state } = useSidebar();

  const member =
    currentShop.viewer ?? currentShop.users.find((u) => u.userId === viewerId);
  if (!member?.warehouseId || member.roleId !== "LOGISTICIAN") return null;

  const location = currentShop.locations?.find(
    (loc) => loc.id === member.warehouseId,
  );
  const name = location?.address?.companyName || location?.address?.address1;

  if (!name) return null;

  if (state === "collapsed") {
    return (
      <div className="flex items-center justify-center py-1">
        <Warehouse className="size-4 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-2 py-1.5 text-xs">
      <Warehouse className="size-3 flex-shrink-0" />
      <span className="truncate">{name}</span>
    </div>
  );
}

export function AppSidebar() {
  const { data: currentShop } = useCurrentShopSuspense();
  const isTrackingEnabled = currentShop.plan.tratrack === true;

  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader
        className={`pt-4 ${isCollapsed ? "items-center flex" : ""}`}
      >
        <SidebarLogo />
        <LogisticianWarehouse />
        <WrappedAccountSwitcher />
      </SidebarHeader>
      <SidebarContent className="flex">
        <SidebarGroup className="flex flex-col gap-2">
          <NewReturnButton />
          <DemoReturnWidgetButton />
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel>Dashboard</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <PrimaryNavigation />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup className="flex-1">
          <SidebarGroupLabel>Settings</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SecondaryNavigation />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupContent className="flex flex-col gap-2">
            {isTrackingEnabled ? (
              <SidebarMenuButton className="py-3 h-auto flex justify-between items-center bg-sidebar-accent">
                <a
                  href={`${import.meta.env.VITE_APP_TRACK_URL?.replace(/\/$/, "")}/shops/${currentShop.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2"
                >
                  <div className="flex aspect-square size-6 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                    <img
                      className="rounded-lg aspect-square w-8"
                      src="/tracking-logo.png"
                    />
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">Tracking</span>
                    <span className="truncate text-xs">Get started</span>
                  </div>
                </a>
                <ExternalLink className="size-4" />
              </SidebarMenuButton>
            ) : null}
            <ChangelogMenu />
            {state === "expanded" && <ScannerButton />}
            <ChangelogWidget />
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  );
}
